import {Component, Input, OnChanges, OnDestroy, EventEmitter, Output, SimpleChanges, HostListener} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    UntypedFormBuilder,
    UntypedFormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR, ValidationErrors
} from '@angular/forms';
import {ImageService} from '../../providers/service/image.service';
import {DomSanitizer} from '@angular/platform-browser';
import {
    arrayIsSet,
    hunaParseFileName,
    isNotNullOrUndefined,
    stringIsSet
} from '../../providers/service/global-variables.service';
import {combineLatest, forkJoin, Observable, of, Subject, Subscription} from 'rxjs';
import {coerceNumberProperty} from '@angular/cdk/coercion';
import {fromPromise} from 'rxjs/internal-compatibility';
import {map, startWith} from "rxjs/operators";


export type RejectReason = 'type' | 'size' | 'no_multiple';

import * as Papa from 'papaparse';
import {MatDialog} from "@angular/material/dialog";

@Component({
    selector: 'app-upload-file',
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: UploadFileComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: UploadFileComponent
        }
    ],
    standalone: false
})
export class UploadFileComponent implements OnChanges, OnDestroy, ControlValueAccessor {
    form: UntypedFormGroup;
    subScriptions = new Subscription()
    enableDisabelEmitEvent = false;
    visualizeImage = false;
    public url$: Observable<string | undefined>
    public name$: Observable<string | undefined>
    public imageNotSet$: Observable<boolean | undefined>
    @Input() title: string | undefined;
    @Input() accept = '*';
    @Input() disabled = false;
    @Input() compressImage = true;
    @Input() loading = false
    @Output() changeValue = new EventEmitter<any>();
    @Output() loadingImage = new EventEmitter<any>();
    @Output() destroyImage = new Subject<any>()

    imageIsDestroyedEmit = new Subject<{ destroyed: boolean, terminateLoad: boolean } | undefined>()
    imageIsDestroyed$ = this.imageIsDestroyedEmit.asObservable()

    @Input() set imageIsDestroyed(destroyed: { destroyed: boolean, terminateLoad: boolean } | undefined) {
        this.imageIsDestroyedEmit.next(destroyed)
    }

    @Input()
    get maxFileSize(): number {
        return this._maxFileSize;
    }

    set maxFileSize(value: number) {
        this._maxFileSize = coerceNumberProperty(value);
    }

    private _maxFileSize: number = coerceNumberProperty(undefined);
    titlePrint = '';
    loadImage = false;

    setLoadImage(value: boolean) {
        this.loadImage = value
        this.loadingImage.next(value)
    }

    _isHovered = false;

    private preventDefault(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    get formIsDisabled(): boolean | undefined {
        return this.form && this.form.get('value')?.invalid;
    }

    @HostListener('dragover', ['$event'])
    _onDragOver(event: any) {
        if (this.disabled || this.formIsDisabled) {
            return;
        }

        this.preventDefault(event);
        this._isHovered = true;
    }

    @HostListener('dragleave')
    _onDragLeave() {
        this._isHovered = false;
    }

    @HostListener('drop', ['$event'])
    _onDrop(event: any) {
        if (this.disabled || this.formIsDisabled) {
            return;
        }
// è possibile importare i file o le directory se conteporaneamente importa le directory
        this.preventDefault(event);
        this._isHovered = false;
        const droppedItems: DataTransferItem[] = event.dataTransfer.items;
        if (droppedItems.length > 0) {
            const droppedFiles: File[] = [];
            const droppedDirectories = [];

            // seperate dropped files from dropped directories for easier handling
            for (let i = 0; i < droppedItems.length; i++) {
                const entry = droppedItems[i].webkitGetAsEntry();
                if (entry?.isFile) {
                    droppedFiles.push(event.dataTransfer.files[i]);
                } else if (entry?.isDirectory) {
                    droppedDirectories.push(entry);
                }
            }

            // create a DataTransfer

            const droppedFilesList = new DataTransfer();
            droppedFiles.forEach((droppedFile) => {
                droppedFilesList.items.add(droppedFile);
            });
            if (droppedDirectories.length) {
                const extractFilesFromDirectoryCalls = [];

                for (const droppedDirectory of droppedDirectories) {
                    extractFilesFromDirectoryCalls.push(this.extractFilesFromDirectory(droppedDirectory));
                }

                // wait for all directories to be proccessed to add the extracted files afterwards
                Promise.all(extractFilesFromDirectoryCalls).then((allExtractedFiles: any[]) => {
                    allExtractedFiles
                        .reduce((a, b) => [...a, ...b])
                        .forEach((extractedFile: File) => {
                            droppedFilesList.items.add(extractedFile);
                        });
                    this.importFileDrop(droppedFilesList.files);
                });
            } else {
                this.importFileDrop(droppedFilesList.files);
            }

        }
    }

    private extractFilesFromDirectory(directory: any) {
        async function getFileFromFileEntry(fileEntry: any) {
            try {
                return await new Promise((resolve, reject) => fileEntry.file(resolve, reject));
            } catch (err) {
                console.error('Error converting a fileEntry to a File: ', err);
                return undefined
            }
        }

        return new Promise((resolve, reject) => {
            const files: File[] = [];

            const dirReader = directory.createReader();

            // we need this to be a recursion because of this issue: https://bugs.chromium.org/p/chromium/issues/detail?id=514087
            const readEntries = () => {
                dirReader.readEntries(async (dirItems: any) => {
                    if (!dirItems.length) {
                        resolve(files);
                    } else {
                        const fileEntries = dirItems.filter((dirItem: any) => dirItem.isFile);

                        for (const fileEntry of fileEntries) {
                            const file: any = await getFileFromFileEntry(fileEntry);
                            files.push(file);
                        }

                        readEntries();
                    }
                });
            };
            readEntries();
        });
    }

    pzProperties = {
        zoomControlScale: 3,
        minScale: 2,
        limitPan: true,
        limitZoom: 20,
        wheelZoomFactor: 1
    };

    constructor(
        private fb: UntypedFormBuilder,
        private imageService: ImageService,
        private _sanitizer: DomSanitizer,
        private matDialog: MatDialog
    ) {
        this.form = this.fb.group({value: null});
        const image$ = this.form.get('value')!.valueChanges.pipe(
            startWith(),
            map(() => this.form.get('value')!.value)
        )
        this.url$ = image$.pipe(
            map(image => {

                let url;
                if (image != null && image.url != null) {
                    if (typeof image.url != "function") {
                        url = image.url
                    } else {
                        url = image.url()
                    }
                }
                return url;
            })
        )
        this.name$ = image$.pipe(
            map(image => {
                let name;
                if (image != null && image.name != null) {
                    if (typeof image.name != "function") {
                        name = image.name
                    } else {
                        name = image.name()
                    }
                }
                return name;
            })
        )
        this.imageNotSet$ = combineLatest([this.name$, this.url$]).pipe(
            startWith([]),
            map(([name, url]) => {
                return url == null
            })
        )
        this.subScriptions.add(
            this.imageIsDestroyed$.subscribe((destroyed => {
                if (destroyed && destroyed.destroyed) {
                    this.unsetValueFile()
                }
                if (destroyed && destroyed.terminateLoad) {
                    this.setLoadImage(false)
                }
            }))
        )
    }

    ngOnChanges(changes: SimpleChanges): void {

    }


    getErrorByObject(errors: any): string {
        if (errors == null || (typeof errors != 'object' && typeof errors != 'string' && errors.toString == null)) {
            return '';
        }
        let errorMessage = '';
        const keys = Object.keys(errors);
        if (arrayIsSet(keys)) {
            keys.forEach((key, index) => {
                if (errors[key] != null) {
                    if (index != 0) {
                        errorMessage += ' ';
                    }
                    errorMessage += key;
                    if (typeof errors[key] == 'object') {
                        errorMessage = ' ' + this.getErrorByObject(errors[key]);
                    } else if (typeof errors[key] == 'string') {
                        errorMessage += ' ' + errors[key];
                    } else if (errors[key].toString) {
                        errorMessage += ' ' + errors[key].toString();
                    }
                }
            });
        }
        return errorMessage;
    }

    ngOnDestroy(): void {
        this.subScriptions.unsubscribe()
    }

    onTouched: Function = () => {
    }


    registerOnChange(onChange: any) {
        const sub = this.form.get('value')?.valueChanges
            .subscribe(onChange);
        if (sub instanceof Subscription) {
            this.subScriptions.add(sub);
        }
    }

    registerOnTouched(onTouched: Function) {
        this.onTouched = onTouched;
    }

    setDisabledState(disabled: boolean) {
        if (disabled) {
            this.form.disable({emitEvent: this.enableDisabelEmitEvent});
        } else {
            this.form.enable({emitEvent: this.enableDisabelEmitEvent});
        }
    }

    writeValue(value: any) {
        if (!isNotNullOrUndefined(value)) {
            this.form.get('value')?.reset();
        } else if (value) {
            this.form.get('value')?.setValue(value);
            this.form.get('value')?.markAsPristine();
            this.form.get('value')?.markAsUntouched();

            setTimeout(() => {
                this.form.get('value')?.updateValueAndValidity();
            }, 20)
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (!isNotNullOrUndefined(this.form.get('value')?.validator)) {
            this.form.get('value')?.setValidators(control.validator);
        }
        if (this.form.get('value')?.valid) {
            return null;
        } else {
            return this.form.get('value')?.errors!;
        }
    }

    get isDisabled(): boolean {
        return this.form.get('value') != null && this.form.get('value')?.disabled == true;
    }

    minimizeImage() {
        this.visualizeImage = !this.visualizeImage;
    }

    unsetValueFile() {
        this.form.get('value')?.reset();
        this.form.get('value')?.markAsTouched();

    }


    getImageForForm(base64: string, nameNotParsed: string) {
        let promiseBase64
        if (this.compressImage) {
            promiseBase64 = this.imageService.compressImage(base64)
            // compressImage always re-encodes to JPEG
            nameNotParsed = nameNotParsed.replace(/\.[^./\\]*$/, '') + '.jpg';
        } else {
            promiseBase64 = new Promise(resolve => {
                resolve(base64)
            })

        }
        return promiseBase64.then(compressed => {
            const base64Compressa: string = compressed as string;
            const url = () => {
                return this._sanitizer.bypassSecurityTrustUrl(base64Compressa);
            };
            const name = () => {
                return hunaParseFileName(nameNotParsed);
            };
            return {
                url: url(),
                file: base64Compressa,
                name
            };
            // this.salvaImage = true;
        });
    }

    onLoadImage(files: File[]) {
        try {
            const fileUploadControl = files[0];
            if (files.length == 0) {
                // this.alertService.error(this.translate.instant('load_correct_file'));
            } else {
                if (fileUploadControl) {
                    const file = fileUploadControl;
                    const name = fileUploadControl.name;
                    if (file.type == 'text/csv') {
                        Papa.parse(file, {
                            comments: '#',
                            skipEmptyLines: true,
                            complete: async (result) => {

                                const name = () => {
                                    return hunaParseFileName(fileUploadControl.name);
                                };

                                const value = {
                                    url: undefined,
                                    file: file,
                                    name,
                                    csv: result
                                };
                                this.form.get('value')?.setValue(value);
                                this.form.get('value')?.markAsTouched();
                                this.setLoadImage(false)
                                this.form.get('value')?.updateValueAndValidity()
                                this.changeValue.next(value)
                            }
                        })
                    } else {
                        const reader = new FileReader();
                        reader.onloadend = (e: ProgressEvent) => {
                            const base64 = (e.target as FileReader).result as any;
                            this.getImageForForm(base64, name).then(
                                (value) => {
                                    this.form.get('value')?.setValue(value);
                                    this.form.get('value')?.markAsTouched();
                                    this.setLoadImage(false)
                                    this.form.get('value')?.updateValueAndValidity()
                                    this.changeValue.next(value)
                                }
                            );
                        };
                        reader.onerror = (e) => {
                            // this.alertService.error(e)
                        };
                        reader.readAsDataURL(file);
                    }

                }
            }
        } catch (e) {
            this.setLoadImage(false);
            console.error(e);
        }
    }

    importFile(event: any) {
        this.setLoadImage(true);
        setTimeout(() => {
            if (event.target.files.length !== 0) {
                const files = Object.keys(event.target.files).map(key => event.target.files[key]);
                this.onLoadImage(files);
            } else {
                this.setLoadImage(false);
            }
        }, 50);
    }

    importFileDrop(files: any) {
        this.setLoadImage(true);
        setTimeout(() => {
            if (files.length !== 0) {
                const filesArray = Object.keys(files).map(key => files[key]);
                this.onLoadImage(filesArray);
            } else {
                this.setLoadImage(false);
            }
        }, 50);
    }


    get uploadDisabled() {
        return this.disabled || this.loading || this.loadImage || (this.form && this.form.get('value') != null && this.form.get('value')?.disabled);
    }

    get url() {
        let url;
        const image = this.form.get('value')!.value
        if (image != null && image.url != null) {
            if (typeof image.url != "function") {
                url = image.url
            } else {
                url = image.url()
            }
        }
        return url;
    }

    openFullImage(containerImage: any) {
        this.matDialog.open(containerImage, {})
            .afterClosed()
            .subscribe()
    }

    deleteImage() {
        this.setLoadImage(true)
        this.destroyImage.next(true)

    }
}
